import { Box, Skeleton } from '@mui/material';
import partition from 'lodash/partition';
import React, { useMemo } from 'react';

import { ProjectConfig, ProjectSalesforceInfo } from '../../../../Types/dealGPT';
import KeyQuestionsCard from './KeyQuestionsCard';
import KpiQuestionsCard from './KpiQuestionsCard';
import TargetCompanyCard from './TargetCompanyCard';

interface ProjectOverviewCardsProps {
  salesforce: ProjectSalesforceInfo | null;
  loadingSalesforce: boolean;
  config: ProjectConfig | null;
  refetch: () => Promise<void>;
}

const ProjectOverviewCards = ({
  salesforce,
  loadingSalesforce,
  config,
  refetch,
}: ProjectOverviewCardsProps) => {
  const [userKpis, projectKpis] = useMemo(
    () => partition(config?.kpiQuestions ?? [], (q) => !!q.createdBy),
    [config?.kpiQuestions]
  );

  const displayTargetCompanyCard = !!salesforce?.industry || !!salesforce?.subIndustry;

  return (
    <Box sx={{ display: 'flex', flex: 1 }}>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: 0.1,
        }}
      >
        {/* Top card */}
        {loadingSalesforce ? (
          <Skeleton variant="rectangular" sx={{ width: '100%', height: '20vh' }} />
        ) : (
          displayTargetCompanyCard && <TargetCompanyCard salesforce={salesforce} />
        )}

        {/* KPI & Key Questions Cards */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {config ? (
            <KpiQuestionsCard
              title="User Basic Facts"
              runId={config.projectRunId}
              kpiQuestions={userKpis}
              refetchProjectConfig={refetch}
              canAddKpis
            />
          ) : (
            <Skeleton variant="rectangular" sx={{ width: '100%', height: '60vh' }} />
          )}

          {config ? (
            <KpiQuestionsCard
              title="Project Basic Facts"
              runId={config.projectRunId}
              kpiQuestions={projectKpis}
              refetchProjectConfig={refetch}
            />
          ) : (
            <Skeleton variant="rectangular" sx={{ width: '100%', height: '60vh' }} />
          )}

          {config ? (
            <KeyQuestionsCard keyQuestions={config.keyQuestions} refetchProjectConfig={refetch} />
          ) : (
            <Skeleton variant="rectangular" sx={{ width: '100%', height: '60vh' }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectOverviewCards;
